import React, { ReactNode, useState } from "react";
import clsx from "clsx";
import { AlertTriangle, ArrowRight, Info, Lightbulb } from "lucide-react";
import { DocsLink } from "./DocsLink";

/**
 * The docs' content blocks (design §0.3–§0.4): callouts by role, numbered steps, link cards and
 * tabs. Markdown reaches them through `markdown/remarkDocs.ts`; generated pages use them
 * directly.
 */

export type CalloutRole = "note" | "warning" | "tip";

const ROLE_STYLE: Record<CalloutRole, { icon: ReactNode; className: string }> = {
  // Warning: irreversible / money / security. Note: scope, default, "not yet", another way. Tip: best practice.
  warning: { icon: <AlertTriangle size={16} />, className: "border-orange bg-orange/10" },
  note: { icon: <Info size={16} />, className: "border-border bg-muted" },
  tip: { icon: <Lightbulb size={16} />, className: "border-purpleGreen bg-purpleGreen/10" },
};

export const Callout = ({ role = "note", title, children }: { role?: CalloutRole; title?: string; children?: ReactNode }) => {
  const style = ROLE_STYLE[role] ?? ROLE_STYLE.note;

  return (
    <div className={clsx("my-4 flex gap-3 rounded-lg border-l-4 px-4 py-3 text-bodySmall", style.className)} data-callout={role}>
      <span className="mt-0.5 shrink-0 text-foreground">{style.icon}</span>
      <div className="min-w-0 [&>p:first-child]:mt-0 [&>p:last-child]:mb-0">
        {title ? <p className="mb-1 font-semibold text-foreground">{title}</p> : null}
        {children}
      </div>
    </div>
  );
};

/** Numbered steps: each `###` heading inside starts a step (CSS counters keep the numbers). */
export const Steps = ({ children }: { children?: ReactNode }) => (
  <div className="my-6 border-l border-border pl-6 [counter-reset:step] [&_h3]:relative [&_h3]:[counter-increment:step] [&_h3]:before:absolute [&_h3]:before:-left-9 [&_h3]:before:flex [&_h3]:before:h-6 [&_h3]:before:w-6 [&_h3]:before:items-center [&_h3]:before:justify-center [&_h3]:before:rounded-full [&_h3]:before:bg-muted [&_h3]:before:text-bodySmall [&_h3]:before:content-[counter(step)]">
    {children}
  </div>
);

export const Cards = ({ children }: { children?: ReactNode }) => <div className="my-6 grid grid-cols-1 gap-4 clg:grid-cols-2">{children}</div>;

/** A card: a bold line and one sentence (design §0.3); the whole card is the link. */
export const Card = ({ title, href, children }: { title?: string; href?: string; children?: ReactNode }) => {
  const body = (
    <div className="h-full rounded-lg border border-border bg-card p-4 transition-colors hover:border-foreground/40">
      {title ? (
        <p className="flex items-center gap-2 font-semibold text-foreground">
          {title}
          {href ? <ArrowRight size={14} /> : null}
        </p>
      ) : null}
      <div className="mt-1 text-bodySmall text-muted-foreground [&>p]:m-0">{children}</div>
    </div>
  );

  return href ? (
    <DocsLink href={href} className="block no-underline">
      {body}
    </DocsLink>
  ) : (
    body
  );
};

/** Tabs over children marked with a `title` (Markdown: `:::tab{title="…"}`). */
export const Tabs = ({ children }: { children?: ReactNode }) => {
  const tabs = React.Children.toArray(children).filter((child): child is React.ReactElement<{ title?: string; children?: ReactNode }> => React.isValidElement(child));
  const [active, setActive] = useState(0);
  const current = tabs[Math.min(active, tabs.length - 1)];

  return (
    <div className="my-4">
      <div className="flex gap-1 border-b border-border" role="tablist">
        {tabs.map((tab, i) => (
          <button
            key={tab.props.title ?? i}
            type="button"
            role="tab"
            aria-selected={i === active}
            onClick={() => setActive(i)}
            className={clsx("border-b-2 px-3 py-2 text-bodySmall", i === active ? "border-purpleGreen text-foreground" : "border-transparent text-muted-foreground")}
          >
            {tab.props.title}
          </button>
        ))}
      </div>
      <div className="pt-3">{current}</div>
    </div>
  );
};

export const Tab = ({ children }: { title?: string; children?: ReactNode }) => <div>{children}</div>;
